import { useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from '../../../../lib/i18n';
import { patchRoutine, type WebRoutine } from '../../../../lib/routines';
import { activeRecommendedSplit } from '../../../../lib/routine-groups';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useRoutineCopy } from '../../routines/use-routine-copy';
import { Badge, Switch, TextButton, Typography } from '../../ui';
import { CHAT_HOME_BI } from '../message-utils';
import styles from './ConnectorRoutineRows.module.css';

/**
 * One connector's routines as switch rows, split into what is running (Active) and what is offered but
 * off (Recommended for you), each row a title over its subtitle and schedule with a switch on the right.
 *
 * Shared by `PostConnectedConnectorCard` (one app under one reply) and `BandCard` (an app row the reader
 * expanded), because those two draw the SAME rows and a second copy of this would drift the moment either
 * card was touched — the split, the ordering, the bulk turn-on and the toggle are all rules the phones
 * hold too.
 *
 * The Active / Recommended split is `activeRecommendedSplit` (a lib module, unit-testable without this
 * component); the promise sub-group survives only as the sort key inside each section, so related rows
 * still cluster without repeating a heading a single-connector card cannot use.
 */
export function ConnectorRoutineRows({
  routines,
  onToggleReported,
  biPage = CHAT_HOME_BI,
  rowProps,
}: {
  routines: WebRoutine[];
  /** The page the bulk turn-on reports under. The chat cards are the default; the Wins page passes its own. */
  biPage?: { page_name: string; page_version?: string };
  /** Called with the routine and the state it is being moved TO, for the caller's own click event.
   *  Opt-in: `PostConnectedConnectorCard` passes nothing and is byte-identical, so adding the band
   *  card's measurement cannot quietly change what the connector card reports. The bulk turn-on reports
   *  each routine it flips through this same callback. */
  onToggleReported?: (routine: WebRoutine, paused: boolean) => void;
  /** An extra class and style per row, e.g. the chat Status panel's slide-in for a routine that just arrived. */
  rowProps?: (routine: WebRoutine) => { className?: string; style?: CSSProperties } | undefined;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { scheduleText } = useRoutineCopy();
  const [inFlight, setInFlight] = useState<Record<string, boolean>>({});
  const { active, recommended } = useMemo(() => activeRecommendedSplit(routines), [routines]);

  const toggle = async (routine: WebRoutine) => {
    if (!routine.id || inFlight[routine.id]) return;
    // Reported on the INTENT, not on the response: the patch is optimistic and rolls back on failure, so
    // waiting for it would drop every flip the server refused — which is the half worth seeing.
    onToggleReported?.(routine, !routine.paused);
    setInFlight((current) => ({ ...current, [routine.id]: true }));
    // patchRoutine is optimistic and rolls back on failure, so a refused flip leaves the switch showing
    // what is really running — which is this row's whole job. No local mirror of the state to drift.
    await patchRoutine(routine, { paused: !routine.paused });
    setInFlight((current) => ({ ...current, [routine.id]: false }));
  };

  // Turn every recommended (paused) routine ON at once. Each flip goes through the same optimistic
  // patch and the same reporting as a single toggle, so a refused one rolls its own switch back and the
  // rest still land — the bulk action is N toggles, not one write that can leave the section half-done
  // with no record of which flips took.
  const turnOnAll = () => {
    // The bulk action is its own CTA, logged once here — the per-routine flips report through
    // onToggleReported (opt-in), which the post-connected card does not pass, so without this the bulk
    // click would go unrecorded on that surface.
    logPageClickBi({ ...biPage, button_name: 'connector-routines-turn-on-all' }, { additionalFields: { count: String(recommended.length) } });
    for (const routine of recommended) {
      if (!routine.paused) continue;
      void toggle(routine);
    }
  };
  const bulkInFlight = recommended.some((routine) => routine.id && inFlight[routine.id]);

  if (active.length === 0 && recommended.length === 0) return null;

  const row = (routine: WebRoutine) => {
    const schedule = scheduleText(routine);
    return (
      <li
        key={routine.id}
        className={[styles.routine, rowProps?.(routine)?.className].filter(Boolean).join(' ')}
        style={rowProps?.(routine)?.style}
      >
        <div className={styles.text}>
          <Typography variant="h4" className={styles.title}>
            {routine.name ?? ''}
          </Typography>
          {routine.description && (
            <Typography variant="p2" className={styles.subtitle}>
              {routine.description}
            </Typography>
          )}
          {schedule && (
            <Typography variant="p2" className={styles.schedule}>
              {schedule}
            </Typography>
          )}
        </div>
        <Switch
          className={styles.toggle}
          checked={!routine.paused}
          disabled={Boolean(routine.id && inFlight[routine.id])}
          onChange={() => void toggle(routine)}
          aria-label={routine.name ?? ''}
        />
      </li>
    );
  };

  return (
    <>
      {active.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHead}>
            {/* Green pill: the Figma fill is #DEFFC8 / text #3D7321, which is the DS `done` badge — NOT
                the mint `active` variant (#D2FBF2). The label is the section's word, the colour its token. */}
            <Badge variant="done" label={t('connectors.card.active')} />
          </div>
          <ul className={styles.routines}>{active.map(row)}</ul>
        </div>
      )}
      {recommended.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHead}>
            <Badge label={t('connectors.card.recommended')} />
            {recommended.length > 1 && (
              <TextButton size="small" disabled={bulkInFlight} onClick={turnOnAll} className={styles.turnOnAll}>
                {t('connectors.card.turnOnAll', { count: recommended.length })}
              </TextButton>
            )}
          </div>
          <ul className={styles.routines}>{recommended.map(row)}</ul>
        </div>
      )}
    </>
  );
}
